import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { getRoleDisplayName } from '../utils/auth';
import { useToast } from './ToastContext';

const EyeIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeOffIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

const ROLES = [
  { value: 'superadmin', label: 'Superadmin' },
  { value: 'ar', label: 'Accounting Representative - Accounting Receivable' },
  { value: 'as', label: 'Account Supervisor (AS)' },
  { value: 'asm', label: 'Area Sales Manager (ASM)' },
  { value: 'credit_officer', label: 'Credit Collection Officer' },
  { value: 'accounting_manager', label: 'Accounting Manager (View Only)' },
  { value: 'accounting_super_manager', label: 'Accounting Senior Manager (View Only)' },
  { value: 'regional_sales_manager', label: 'Regional Sales Manager (View Only)' },
  { value: 'cco', label: 'Chief Operating Officer' },
  { value: 'ceo', label: 'Chief Executive Officer (CEO)' }
];

function UserManagement() {
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  
  // Toast notifications
  const { showToast } = useToast();

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage] = useState(15);

  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);

  const [formData, setFormData] = useState({
    username: '',
    full_name: '',
    role: 'ar',
    company_id: '',
    password: '',
    is_active: true
  });

  const [resetPassword, setResetPassword] = useState('');
  const [showCreatePassword, setShowCreatePassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchCompanies();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (err) {
      setMessageType('error');
      setMessage(err.response?.data?.message || 'Failed to load users.');
    } finally {
      setLoading(false);
      setCurrentPage(1);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await api.get('/companies');
      setCompanies(response.data);
    } catch (err) {
      console.error('Failed to load companies:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const resetForm = () => {
    setFormData({
      username: '',
      full_name: '',
      role: 'ar',
      company_id: '',
      password: '',
      is_active: true
    });
    setEditingUser(null);
    setShowForm(false);
    setShowCreatePassword(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');

    try {
      if (editingUser) {
        await api.put(`/users/${editingUser.id}`, {
          full_name: formData.full_name,
          role: formData.role,
          company_id: formData.company_id || null,
          is_active: formData.is_active
        });
        showToast('✓ User updated successfully!', 'success', 4000);
        setMessage('User updated successfully.');
      } else {
        await api.post('/users', formData);
        showToast('✓ New user created successfully!', 'success', 4000);
        setMessage('User created successfully.');
      }
      resetForm();
      fetchUsers();
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Failed to save user.';
      showToast(`✗ ${errorMsg}`, 'error', 8000);
      setMessageType('error');
      setMessage(errorMsg);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      full_name: user.full_name,
      role: user.role,
      company_id: user.company_id || '',
      password: '',
      is_active: user.is_active
    });
    setShowForm(true);
  };

  const handleToggleActive = async (user) => {
    if (!window.confirm(`Are you sure you want to ${user.is_active ? 'deactivate' : 'activate'} ${user.full_name}?`)) {
      return;
    }

    try {
      const response = await api.post(`/users/${user.id}/toggle-active`);
      showToast(response.data.message.includes('active') 
        ? '✓ User activated successfully!' 
        : '✓ User deactivated successfully!', 'success', 4000);
      setMessageType('success');
      setMessage(response.data.message);
      fetchUsers();
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Failed to toggle user status.';
      showToast(`✗ ${errorMsg}`, 'error', 8000);
      setMessageType('error');
      setMessage(errorMsg);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!resetPassword || resetPassword.length < 6) {
      showToast('✗ Password must be at least 6 characters.', 'error', 5000);
      setMessageType('error');
      setMessage('Password must be at least 6 characters.');
      return;
    }

    try {
      await api.post(`/users/${resetPasswordUser.id}/reset-password`, { newPassword: resetPassword });
      showToast('✓ Password reset successfully! User can now login with new password.', 'success', 6000);
      setMessageType('success');
      setMessage(`Password for ${resetPasswordUser.full_name} has been reset.`);
      setResetPasswordUser(null);
      setResetPassword('');
      setShowResetPassword(false);
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Failed to reset password.';
      showToast(`✗ ${errorMsg}`, 'error', 8000);
      setMessageType('error');
      setMessage(errorMsg);
    }
  };

  const getCompanyName = (companyId) => {
    if (!companyId) return 'All Branches';
    const company = companies.find(c => c.id === companyId);
    return company ? company.name : 'Unknown';
  };

  // Pagination calculations
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = users.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(users.length / usersPerPage);

  return (
    <div className="user-management-container">
      <div className="page-header">
        <h1>User Management</h1>
        <button
          className="btn btn-primary"
          onClick={() => {
            resetForm();
            setShowForm(true);
          }}
        >
          + Add New User
        </button>
      </div>

      {message && (
        <div className={`alert ${messageType === 'success' ? 'alert-success' : 'alert-error'}`}>
          {message}
        </div>
      )}

      {showForm && (
        <div className="modal-overlay" onClick={resetForm}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingUser ? 'Edit User' : 'Add New User'}</h3>
              <button className="close-btn" onClick={resetForm}>&times;</button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleSubmit}>
                <div className="form-row" style={{ marginBottom: '16px', gap: '16px' }}>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>Username</label>
                    <input
                      type="text"
                      name="username"
                      value={formData.username}
                      onChange={handleInputChange}
                      className="form-control"
                      required
                      disabled={!!editingUser}
                    />
                  </div>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>Full Name</label>
                    <input
                      type="text"
                      name="full_name"
                      value={formData.full_name}
                      onChange={handleInputChange}
                      className="form-control"
                      required
                    />
                  </div>
                </div>

                <div className="form-row" style={{ marginBottom: '16px', gap: '16px' }}>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>Role</label>
                    <select
                      name="role"
                      value={formData.role}
                      onChange={handleInputChange}
                      className="form-control"
                      required
                    >
                      {ROLES.map(role => (
                        <option key={role.value} value={role.value}>{role.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>Company</label>
                    <select
                      name="company_id"
                      value={formData.company_id}
                      onChange={handleInputChange}
                      className="form-control"
                    >
                      <option value="">All Branches</option>
                      {companies.map(company => (
                        <option key={company.id} value={company.id}>{company.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {!editingUser && (
                  <div className="form-group" style={{ marginBottom: '16px' }}>
                    <label>Password</label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type={showCreatePassword ? 'text' : 'password'}
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        className="form-control"
                        required={!editingUser}
                        minLength={6}
                        style={{ paddingRight: '40px' }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowCreatePassword(!showCreatePassword)}
                        style={{
                          position: 'absolute',
                          right: '10px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '4px',
                          color: '#666'
                        }}
                      >
                        {showCreatePassword ? EyeOffIcon : EyeIcon}
                      </button>
                    </div>
                  </div>
                )}

                <div className="form-group" style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={formData.is_active}
                      onChange={handleInputChange}
                    />
                    Active Account
                  </label>
                </div>

                <div className="modal-actions" style={{ gap: '12px' }}>
                  <button type="button" className="btn btn-secondary" onClick={resetForm} style={{ flex: 1 }}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>
                    {editingUser ? 'Update User' : 'Create User'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {resetPasswordUser && (
        <div className="modal-overlay" onClick={() => { setResetPasswordUser(null); setShowResetPassword(false); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Reset Password</h3>
              <button className="close-btn" onClick={() => { setResetPasswordUser(null); setShowResetPassword(false); }}>&times;</button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: '16px' }}>
                Reset password for <strong>{resetPasswordUser.full_name}</strong> ({resetPasswordUser.username})
              </p>
              <form onSubmit={handleResetPassword}>
                <div className="form-group" style={{ marginBottom: '20px' }}>
                  <label>New Password</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showResetPassword ? 'text' : 'password'}
                      value={resetPassword}
                      onChange={(e) => setResetPassword(e.target.value)}
                      className="form-control"
                      required
                      minLength={6}
                      autoFocus
                      style={{ paddingRight: '40px' }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowResetPassword(!showResetPassword)}
                      style={{
                        position: 'absolute',
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '4px',
                        color: '#666'
                      }}
                    >
                      {showResetPassword ? EyeOffIcon : EyeIcon}
                    </button>
                  </div>
                </div>
                <div className="modal-actions" style={{ gap: '12px' }}>
                  <button type="button" className="btn btn-secondary" onClick={() => { setResetPasswordUser(null); setShowResetPassword(false); }} style={{ flex: 1 }}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>
                    Reset Password
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="loading">Loading users...</div>
      ) : (
        <div className="card">
          <table className="table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Username</th>
                <th>Full Name</th>
                <th>Role</th>
                <th>Company</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan="7" className="empty-state">No users found.</td>
                </tr>
              ) : (
                currentUsers.map(user => (
                  <tr key={user.id} className={!user.is_active ? 'inactive-row' : ''}>
                    <td>{user.id}</td>
                    <td>{user.username}</td>
                    <td>{user.full_name}</td>
                    <td>{getRoleDisplayName(user.role)}</td>
                    <td>{getCompanyName(user.company_id)}</td>
                    <td>
                      <span className={`badge ${user.is_active ? 'badge-success' : 'badge-danger'}`}>
                        {user.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => handleEdit(user)}
                          style={{ backgroundColor: '#5a9fd4', borderColor: '#5a9fd4' }}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-sm btn-warning"
                          onClick={() => setResetPasswordUser(user)}
                        >
                          Reset Password
                        </button>
                        <button
                          className={`btn btn-sm ${user.is_active ? 'btn-danger' : 'btn-success'}`}
                          onClick={() => handleToggleActive(user)}
                        >
                          {user.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div className="pagination-controls">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="btn btn-sm btn-primary"
                style={{ backgroundColor: '#e63740', borderColor: '#e63740' }}
              >
                Previous
              </button>

              <span className="pagination-info">
                Page {currentPage} of {totalPages} ({users.length} records)
              </span>

              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="btn btn-sm btn-primary"
                style={{ backgroundColor: '#e63740', borderColor: '#e63740' }}
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default UserManagement;
